import {FilterWrapper, useListFilters, useListOrderBy} from '@hconnect/common/maintain-filters'
import {AnalysisListType} from '@hconnect/common/types'
import {useTranslation} from '@hconnect/uikit'
import {PageContainer} from '@hconnect/uikit/src/lib2'
import {H1} from '@hm/fundamental'
import {Box} from '@mui/material'
import {useState} from 'react'
import {useSearchParams} from 'react-router'

import {useHasRcfaAdminRole} from '../../hooks/useHasRcfaAdminRole'
import {FEATURE_NAMESPACE} from '../../localization'
import {usePlantUpmId} from '../common'

import {getAvailableFilters} from './analysisList.filters'
import {getAvailableColumns} from './analysisList.orderBy'
import {AnalysisListProperty} from './analysisList.types'
import {AnalysisListSwitch} from './AnalysisListSwitch'
import {AnalysisListTable} from './AnalysisListTable'
import {NewAnalysisButton} from './NewAnalysisButton'

export const AnalysisListPage = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const plantUpmId = usePlantUpmId()
  const canReassign = useHasRcfaAdminRole()

  const [canCreateAnalysis, setCanCreateAnalysis] = useState<boolean>(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const page = parseInt(searchParams.get('page') || '0')
  const rowsNumber = parseInt(searchParams.get('rows') || '10')
  const listType = (searchParams.get('type') as AnalysisListType) || AnalysisListType.Mine

  const availableFilters = getAvailableFilters(listType, canReassign, plantUpmId)
  const availableColumns = getAvailableColumns(listType, canReassign)

  const {filterState, apiFilters, dispatch} = useListFilters(
    availableFilters,
    searchParams.get('filters')
  )

  const {listOrder, handleOrderToggle} = useListOrderBy<AnalysisListProperty>(
    availableColumns.map((column) => column.id),
    searchParams.get('orderBy')
  )

  const handleListTypeChange = (newListType: AnalysisListType) => {
    if (!newListType) return
    setSearchParams((params) => {
      params.set('page', '0')
      params.set('type', newListType)
      return params
    })
  }

  return (
    <PageContainer>
      <Box display="flex" mb={3} flexWrap="wrap" gap={2} justifyContent="space-between">
        <H1 className="text-foreground_inverted">{t('pages.mainTitle')}</H1>
        <FilterWrapper
          filters={filterState}
          dispatch={dispatch}
          translationNamespace={FEATURE_NAMESPACE}
        />
      </Box>

      <Box
        sx={(theme) => ({
          background: theme.palette.common.white,
          color: theme.palette.common.black,
          borderRadius: 1,
          padding: 1.5,
          position: 'relative',
          overflowX: 'auto',
          fontFamily: 'Inter'
        })}
      >
        <Box sx={{marginBottom: '24px'}}>
          <AnalysisListSwitch value={listType} onChange={handleListTypeChange} />
          {canCreateAnalysis && listType === AnalysisListType.Mine && <NewAnalysisButton />}
        </Box>
        <AnalysisListTable
          listType={listType}
          page={page}
          rowsNumber={rowsNumber}
          setCanCreateAnalysis={setCanCreateAnalysis}
          filters={apiFilters}
          order={listOrder}
          handleOrderToggle={handleOrderToggle}
          availableColumns={availableColumns}
        />
      </Box>
    </PageContainer>
  )
}
